import React, { Component, Fragment } from 'react'
import { FormattedMessage } from 'react-intl'
import { orderPickUpType } from 'shared/utils'
import styled from 'styled-components'
import { fonts, H4, H6, SmallText, Text } from 'app/common/typography'
import { ButtonSmall } from 'app/assets/styles'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import { COLOR } from 'app-v2/design-tokens'
import {
    CircleChecked,
    CircleInfo,
    Close,
    Shop,
    TerraceTable
} from 'app/assets/icons'
import { Formik } from 'formik'
import { boolean, number, object } from 'yup'
import { Error, Input } from 'app/common/forms'

const { yellow, red, shadow } = oldColors

type Props = {
    handleClick: (pickUpInfo?: string, pickUpType?: string) => any,
    handleClose: () => void,
    hasTerrace?: boolean
}

type State = {
    clueVisible: boolean
}

export class TableServiceModal extends Component<Props, State> {
    state = {
        clueVisible: false
    }

    formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        const tableRegex = 'clickandcollect.confirmation.table.regex'
        return object().shape({
            table: number()
                .required(mandatory)
                .min(0, tableRegex)
                .max(999, tableRegex),
            terrace: boolean().required(mandatory)
        })
    }

    toggleClue = () => {
        const { clueVisible } = this.state
        this.setState({ clueVisible: !clueVisible })
    }

    onSubmit = async (values: { table: number, terrace: boolean }) => {
        const { handleClick, handleClose } = this.props
        const { table, terrace } = values
        handleClick(
            `${table}${terrace ? '&terrace=' + terrace.toString() : ''}`
        )
        handleClose()
    }

    renderPositionChoice = (values: any, setFieldValue: any) => (
        <Fragment>
            <TextContainer>
                <FormattedMessage id="clickandcollect.confirmation.table.position">
                    {(text: string) => <Text center>{text}</Text>}
                </FormattedMessage>
            </TextContainer>
            <Position>
                <TileContainer onClick={() => setFieldValue('terrace', false)}>
                    <RowContainer>
                        <FormattedMessage id="clickandcollect.confirmation.table.indoor">
                            {(text: string) => (
                                <StyledText center>{text}</StyledText>
                            )}
                        </FormattedMessage>
                        <IconTopContainer>
                            <CircleChecked
                                size={25}
                                background={
                                    values.terrace === false
                                        ? colors.crunchyGreen
                                        : colors.lightGrey
                                }
                            />
                        </IconTopContainer>
                    </RowContainer>
                    <IconContainer>
                        <Shop
                            size={70}
                            color={shadow}
                            style={{ alignSelf: 'center' }}
                        />
                    </IconContainer>
                </TileContainer>
                <TileContainer onClick={() => setFieldValue('terrace', true)}>
                    <RowContainer>
                        <FormattedMessage id="clickandcollect.confirmation.table.terrace">
                            {(text: string) => (
                                <StyledText center>{text}</StyledText>
                            )}
                        </FormattedMessage>
                        <IconTopContainer>
                            <CircleChecked
                                size={25}
                                background={
                                    values.terrace
                                        ? colors.crunchyGreen
                                        : colors.lightGrey
                                }
                            />
                        </IconTopContainer>
                    </RowContainer>
                    <IconContainer>
                        <TerraceTable
                            size={70}
                            color={shadow}
                            style={{ alignSelf: 'center' }}
                        />
                    </IconContainer>
                </TileContainer>
            </Position>
        </Fragment>
    )

    render() {
        const { handleClick, handleClose, hasTerrace } = this.props
        const { clueVisible } = this.state

        return (
            <Formik
                initialValues={{
                    table: '',
                    terrace: hasTerrace ? null : false
                }}
                enableReinitialize
                onSubmit={(values, actions) => this.onSubmit(values)}
                validationSchema={this.formSubscription}>
                {({ handleSubmit, setFieldValue, values, errors, touched }) => (
                    <ResponsiveContainerPadding>
                        <CloseButtonContainer onClick={handleClose}>
                            <Close color={COLOR.PRIMARY} size={24} />
                        </CloseButtonContainer>
                        <TextContainer>
                            <FormattedMessage id="clickandcollect.confirmation.table.title">
                                {(text: string) => <H4 center>{text}</H4>}
                            </FormattedMessage>
                        </TextContainer>
                        <TextContainer>
                            <FormattedMessage id="clickandcollect.confirmation.table.message">
                                {(text: string) => <Text center>{text}</Text>}
                            </FormattedMessage>
                        </TextContainer>
                        <InputContainer>
                            <StyledInput
                                name="table"
                                type="number"
                                placeholderTextColor={colors.lightGrey}
                                placeholderKey={
                                    'clickandcollect.confirmation.table.placeholder'
                                }
                            />
                            <CircleInfoContainer onClick={this.toggleClue}>
                                <CircleInfo color={yellow} size={35} />
                            </CircleInfoContainer>
                        </InputContainer>
                        {errors.table && touched.table && (
                            <Error errorKey={errors.table} />
                        )}
                        {clueVisible && (
                            <ClueTextContainer>
                                <FormattedMessage id="clickandcollect.confirmation.table.clue">
                                    {(text: string) => (
                                        <SmallText>{text}</SmallText>
                                    )}
                                </FormattedMessage>
                            </ClueTextContainer>
                        )}
                        {hasTerrace &&
                            this.renderPositionChoice(values, setFieldValue)}
                        <ButtonsContainer>
                            <StyledButtonSmall
                                type="submit"
                                onClick={handleSubmit}
                                disabled={
                                    values.table === '' ||
                                    values.terrace === null
                                }>
                                <FormattedMessage id="clickandcollect.confirmation.table.validate">
                                    {(text: string) => (
                                        <H6 color="white">{text}</H6>
                                    )}
                                </FormattedMessage>
                            </StyledButtonSmall>
                            <StyledButtonSmall
                                no
                                onClick={() => {
                                    handleClick(
                                        undefined,
                                        orderPickUpType.TAKEAWAY
                                    )
                                    handleClose()
                                }}>
                                <FormattedMessage id="clickandcollect.confirmation.table.takeaway">
                                    {(text: string) => (
                                        <H6 color={red}>{text}</H6>
                                    )}
                                </FormattedMessage>
                            </StyledButtonSmall>
                        </ButtonsContainer>
                    </ResponsiveContainerPadding>
                )}
            </Formik>
        )
    }
}

const ResponsiveContainerPadding = styled.div`
    min-width: 200px;
    padding: 20px;
    background: white;
    align-items: center;
    border-radius: 10px;
`

const CloseButtonContainer = styled.div`
    position: absolute;
    top: 20px;
`

const ButtonsContainer = styled.div`
    width: 100%;
    margin: 30px 0 15px;
`

const StyledButtonSmall = styled(ButtonSmall)`
    border: none;
    ${props =>
        props.no
            ? `background-color: white; border: solid 1px ${red}; margin-top: 20px;`
            : `background-color: ${yellow};`};
    &:disabled {
        opacity: 0.5;
    }
    border-radius: 10px;
    display: block;
    width: 100%;
`

const TextContainer = styled.div`
    margin-bottom: 15px;
    text-align: center;
`

const InputContainer = styled.div`
    position: relative;
    border-color: ${shadow};
    border-width: 1px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: 15px;
`

const StyledInput = styled(Input)`
    margin-top: 5px;
    font-family: ${fonts.roboto};
    font-size: 14px;
    min-height: 50px;
    width: 100%;
    flex: 1;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
    }
`

const CircleInfoContainer = styled.div`
    right: 10px;
    top: 20px;
    position: absolute;
`

const ClueTextContainer = styled.div`
    margin-bottom: 20px;
`

const RowContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
`

const Position = styled(RowContainer)`
    margin: 25px 0 20px;
`

const IconTopContainer = styled.div`
    margin-left: auto;
`

const TileContainer = styled.div`
    width: 120px;
    cursor: pointer;
    padding: 10px;
    border: solid 1px ${shadow};
    border-radius: 5px;
    margin: 0 10px;
`

const StyledText = styled(Text)`
    text-transform: uppercase;
    font-family: ${fonts.flameRegular};
`

const IconContainer = styled.div`
    display: flex;
    justify-content: center;
`
